/**
 * Shop The Look Component
 * 
 * Features:
 * - Collection toggle switcher
 * - Dynamic product grid
 * - Direct linking to collection pages
 * - URL hash support for preserving tab state
 */

class ShopTheLook extends HTMLElement {
  constructor() {
    super();
    
    // Initialize with empty properties that will be set in connectedCallback
    this.toggleContainer = null;
    this.toggleButtons = null;
    this.collectionGrids = null;
    
    this.controller = new AbortController();
    
    // Bind methods to preserve context
    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleHashChange = this.handleHashChange.bind(this);
    this.initFromHash = this.initFromHash.bind(this);
  }

  connectedCallback() {
    // Initialize DOM element references
    console.log('ShopTheLook connectCallback called');
    
    this.toggleContainer = this.querySelector('[data-shop-the-look-toggle]');
    this.toggleButtons = this.querySelectorAll('[data-target]');
    this.collectionGrids = this.querySelectorAll('[data-collection-grid]');

    // Log critical elements for debugging
    console.log('ShopTheLook toggle elements:', {
      toggleContainer: this.toggleContainer ? true : false,
      toggleButtons: this.toggleButtons?.length || 0,
      collectionGrids: this.collectionGrids?.length || 0
    });
    
    // Initialize toggle buttons
    if (this.toggleButtons && this.toggleButtons.length) {
      this.toggleButtons.forEach(button => {
        button.addEventListener('click', this.handleToggleClick, { signal: this.controller.signal });
      });
    }
    
    // Listen for hash changes
    window.addEventListener('hashchange', this.handleHashChange, { signal: this.controller.signal });
    
    // Use both DOMContentLoaded and load events to ensure hash is processed
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.initFromHash, { signal: this.controller.signal, once: true });
    } else {
      this.initFromHash();
    }
    
    // Also try on window load as a fallback
    window.addEventListener('load', this.initFromHash, { signal: this.controller.signal, once: true });
    
    // Immediate attempt to handle hash
    this.handleHashChange();
  }
  
  /**
   * Handle toggle button click to switch between collections
   */
  handleToggleClick(event) {
    console.log('Toggle click event fired');
    const target = event.currentTarget.dataset.target;
    
    if (!target) {
      console.error('No target found in button data attributes');
      return;
    }
    
    this.activateTab(target);
    
    // Update URL hash without scrolling
    const currentHash = window.location.hash.replace('#', '');
    if (currentHash !== target) {
      // Use history API instead of direct assignment to avoid scroll issues
      history.replaceState(null, '', `#${target}`);
    }
    
    // Prevent default scrolling behavior
    setTimeout(() => {
      if (window.scrollY === 0 && window.location.hash) {
        window.scrollTo(0, 0);
      }
    }, 1);
    
    console.log('Toggle click completed');
  }
  
  // All methods related to the modal and product rendering have been removed since we're now
  // directly linking to collection pages instead of using a modal popup
  
  
  // All modal-related methods have been removed
  
  /**
   * Activate a specific tab by its target ID
   */
  activateTab(target) {
    if (!target) return;
    
    console.log(`Activating tab: ${target}`);
    
    // Update toggle buttons
    if (this.toggleButtons && this.toggleButtons.length) {
      this.toggleButtons.forEach(button => {
        const isActive = button.dataset.target === target;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive.toString());
      });
    } else {
      console.error('No toggle buttons found');
    }
    
    // Update collection grids
    if (this.collectionGrids && this.collectionGrids.length) {
      this.collectionGrids.forEach(grid => {
        const isActive = grid.dataset.collectionGrid === target;
        grid.classList.toggle('is-active', isActive);
        grid.setAttribute('aria-hidden', (!isActive).toString());
      });
    } else {
      console.error('No collection grids found');
    }
  }
  
  /**
   * Initialize from the current hash (used on page load)
   */
  initFromHash() {
    const hash = window.location.hash.replace('#', '');
    console.log('Initializing from hash:', hash);
    
    if (hash && this.toggleButtons && this.toggleButtons.length) {
      // Find the button that matches the hash
      const targetButton = Array.from(this.toggleButtons).find(button => 
        button.dataset.target === hash
      );
      
      if (targetButton) {
        console.log(`Found matching button for hash #${hash}`);
        this.activateTab(targetButton.dataset.target);
      }
    }
  }
  
  /**
   * Handle hash change events to update the active tab
   */
  handleHashChange() {
    // Get the current hash without the # symbol
    const hash = window.location.hash.replace('#', '');
    console.log('Handling hash change:', hash);
    
    // If we have a valid hash that matches one of our targets
    if (hash && this.toggleButtons && this.toggleButtons.length) {
      // Find the button that matches the hash
      const targetButton = Array.from(this.toggleButtons).find(button => 
        button.dataset.target === hash
      );
      
      // If we found a matching button, activate that tab
      if (targetButton) {
        this.activateTab(targetButton.dataset.target);
      }
    }
  }
  
  disconnectedCallback() {
    this.controller.abort();
  }
}

if (!customElements.get('shop-the-look')) {
  customElements.define('shop-the-look', ShopTheLook);
}